import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import ModuleLayout from '../common/ModuleLayout';
import SafeIcon from '../../common/SafeIcon';

const { FiBookOpen, FiCalendar, FiTarget, FiHeart, FiCheckCircle } = FiIcons;

const ModuleThree = ({ data, onSave, completed }) => {
  const [formData, setFormData] = useState({
    projectScope: 'new', // new, restart, finish
    targetWordCount: '',
    estimatedChapters: '',
    bookType: 'standalone', // standalone, series
    idealStartDate: '',
    idealFinishDate: '',
    knownConstraints: '',
    sessionsPerWeek: '',
    wordsPerSession: '',
    preferredDays: [],
    bufferWeeks: '',
    flexibilityNotes: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData(prev => ({
        ...prev,
        ...data,
        preferredDays: data.preferredDays || []
      }));
    }
  }, [data]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleDay = (day) => {
    setFormData(prev => {
      const days = prev.preferredDays.includes(day)
        ? prev.preferredDays.filter(d => d !== day)
        : [...prev.preferredDays, day];
      return { ...prev, preferredDays: days };
    });
  };

  const handleSave = async (markComplete) => {
    setIsLoading(true);
    setTimeout(() => {
      onSave(formData, markComplete);
      setIsLoading(false);
    }, 800);
  };

  const requiredFields = [
    'targetWordCount', 'estimatedChapters', 'idealStartDate', 
    'idealFinishDate', 'sessionsPerWeek', 'wordsPerSession'
  ];
  
  const canComplete = requiredFields.every(field => 
    formData[field] && formData[field].toString().trim() !== ''
  ) && formData.preferredDays.length > 0;

  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <ModuleLayout
      title="Build Your Book Plan"
      description="Turn vision into a realistic, grace-filled plan that fits real life."
      icon={FiBookOpen}
      onSave={handleSave}
      completed={completed}
      canComplete={canComplete}
      isLoading={isLoading}
    >
      <div className="space-y-8">
        {/* Project Scope & Type */}
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <SafeIcon icon={FiTarget} />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Project Definition</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Project Scope</label>
              <div className="flex flex-wrap gap-3">
                {['new', 'restart', 'finish'].map(scope => (
                  <button
                    key={scope}
                    onClick={() => handleInputChange('projectScope', scope)}
                    className={`px-4 py-2 rounded-xl border-2 transition-all font-bold capitalize ${
                      formData.projectScope === scope 
                      ? 'border-blue-500 bg-blue-50 text-blue-700' 
                      : 'border-slate-100 bg-slate-50 text-slate-500 hover:border-slate-200'
                    }`}
                  >
                    {scope}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Format</label>
              <div className="flex flex-wrap gap-3">
                {['standalone', 'series'].map(type => (
                  <button
                    key={type}
                    onClick={() => handleInputChange('bookType', type)}
                    className={`px-4 py-2 rounded-xl border-2 transition-all font-bold capitalize ${
                      formData.bookType === type 
                      ? 'border-blue-500 bg-blue-50 text-blue-700' 
                      : 'border-slate-100 bg-slate-50 text-slate-500 hover:border-slate-200'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Target Word Count</label>
              <input
                type="number"
                value={formData.targetWordCount}
                onChange={(e) => handleInputChange('targetWordCount', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                placeholder="e.g., 50000"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Estimated Chapters</label>
              <input
                type="number"
                value={formData.estimatedChapters}
                onChange={(e) => handleInputChange('estimatedChapters', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                placeholder="e.g., 12"
              />
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-orange-50 text-orange-600 rounded-lg">
              <SafeIcon icon={FiCalendar} />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Timeline</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Ideal Start Date</label>
              <input
                type="date"
                value={formData.idealStartDate}
                onChange={(e) => handleInputChange('idealStartDate', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Ideal Finish Date</label>
              <input
                type="date"
                value={formData.idealFinishDate}
                onChange={(e) => handleInputChange('idealFinishDate', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Known Constraints</label>
            <textarea
              value={formData.knownConstraints}
              onChange={(e) => handleInputChange('knownConstraints', e.target.value)}
              rows={2}
              className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
              placeholder="Holidays, travel, busy seasons..."
            />
          </div>
        </div>

        {/* Writing Targets */}
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
              <SafeIcon icon={FiBookOpen} />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Writing Targets</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Sessions Per Week</label>
              <input
                type="number"
                value={formData.sessionsPerWeek}
                onChange={(e) => handleInputChange('sessionsPerWeek', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                placeholder="e.g., 3"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Estimated Words Per Session</label>
              <input
                type="number"
                value={formData.wordsPerSession}
                onChange={(e) => handleInputChange('wordsPerSession', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                placeholder="e.g., 1000"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Preferred Writing Days</label>
            <div className="flex flex-wrap gap-2">
              {daysOfWeek.map(day => (
                <button
                  key={day}
                  onClick={() => toggleDay(day)}
                  className={`flex-1 min-w-[70px] py-3 rounded-xl border-2 transition-all font-bold ${
                    formData.preferredDays.includes(day)
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200'
                  }`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Grace Margin */}
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-green-50 text-green-600 rounded-lg">
              <SafeIcon icon={FiHeart} />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Grace Margin</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-start">
            <div className="md:col-span-1">
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Buffer Weeks</label>
              <input
                type="number"
                value={formData.bufferWeeks}
                onChange={(e) => handleInputChange('bufferWeeks', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                placeholder="e.g., 2"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Notes on Flexibility</label>
              <textarea
                value={formData.flexibilityNotes}
                onChange={(e) => handleInputChange('flexibilityNotes', e.target.value)}
                rows={2}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
                placeholder="How will you handle life's interruptions with grace?"
              />
            </div>
          </div>
        </div>

        {/* Planning Complete Indicator */}
        <AnimatePresence>
          {canComplete && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="bg-green-50 border-2 border-green-100 rounded-2xl p-6 flex items-center justify-center space-x-3"
            >
              <SafeIcon icon={FiCheckCircle} className="text-green-500 text-2xl" />
              <span className="text-green-700 font-bold text-lg">Planning Complete! You're ready to proceed.</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </ModuleLayout>
  );
};

export default ModuleThree;